<template>
    <div>
        <div
            :class="[stateClasses.header, disabled ? '' : 'cursor-pointer']"
            class="flex items-center justify-between gap-3 rounded-lg border px-4 py-3 transition"
            @click.stop="toggleExpanded"
        >
            <div class="flex items-center gap-2 font-semibold">
                <ShieldCheck :stroke-width="2" class="h-4.5 w-4.5 shrink-0" />
                <span class="leading-5">{{ label }}</span>
            </div>
            <div class="flex items-center gap-2">
                <button :class="{ 'cursor-pointer': !disabled }" :disabled="disabled" type="button" @click.stop="confirmStep">
                    <check-circle-icon :active="result === true" class="h-8 w-8" />
                </button>
                <button :class="{ 'cursor-pointer': !disabled }" :disabled="disabled" type="button" @click.stop="rejectStep">
                    <x-circle-icon :active="result === false" class="h-8 w-8" />
                </button>
                <ChevronUp :class="{ 'rotate-180': !isExpanded }" :stroke-width="2" class="h-5 w-5 transition-transform" />
            </div>
        </div>

        <div class="mt-2">
            <div v-if="isExpanded" :class="stateClasses.sideBar" class="border-l-2 pl-4">
                <div v-if="result === false" class="rounded-lg border border-red/30 bg-red/10 p-4 text-red">
                    <div class="flex items-center gap-2 font-semibold">
                        <TriangleAlert :stroke-width="2" class="h-5 w-5" />
                        <span>{{ trans('tasks.safety_inspection.deficiencies_warning.title') }}</span>
                    </div>
                    <ol class="mt-2 list-decimal space-y-1 pl-8 text-sm">
                        <li v-for="(action, actionIndex) in warningActions" :key="actionIndex">{{ action }}</li>
                    </ol>
                </div>

                <p v-if="result === false" :class="stateClasses.hint" class="mt-4 text-sm font-semibold">
                    {{ trans('tasks.safety_inspection.failure_prompt') }}
                </p>

                <div :class="{ 'mt-3': result === false }" class="flex flex-col gap-2">
                    <template v-for="(deficiency, index) in deficiencies" :key="deficiency.id">
                        <button
                            v-if="result === false"
                            :class="[
                                'flex items-start justify-between gap-3 !rounded-lg border px-4 pt-3 pb-1 text-left transition',
                                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                                isSelected(deficiency.id)
                                    ? 'border-red bg-red/10 text-red'
                                    : 'border-panel-border bg-panel text-gray-700 hover:border-red/30',
                            ]"
                            :disabled="disabled"
                            type="button"
                            @click.stop="toggleDeficiency(deficiency.id)"
                        >
                            <div class="flex-1">
                                <div class="font-semibold">{{ index + 1 }}. {{ deficiency.label }}</div>
                                <ul
                                    v-if="deficiency.items?.length"
                                    :class="{ 'text-gray-600': !isSelected(deficiency.id) }"
                                    class="mt-1 list-disc space-y-1 pl-5 text-sm"
                                >
                                    <li v-for="(item, itemIndex) in deficiency.items" :key="itemIndex">{{ item }}</li>
                                </ul>
                            </div>
                            <div
                                :class="isSelected(deficiency.id) ? 'border-red bg-red text-white' : 'border-gray-300 text-gray-400'"
                                class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2"
                            >
                                <X :stroke-width="3" class="h-3 w-3" />
                            </div>
                        </button>
                        <div v-else class="!rounded-lg border border-panel-border bg-panel px-4 pt-3 pb-1 text-gray-700">
                            <div class="font-semibold">{{ index + 1 }}. {{ deficiency.label }}</div>
                            <ul v-if="deficiency.items?.length" class="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-600">
                                <li v-for="(item, itemIndex) in deficiency.items" :key="itemIndex">{{ item }}</li>
                            </ul>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { computed, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { ChevronUp, ShieldCheck, TriangleAlert, X } from '@lucide/vue';
import CheckCircleIcon from '@/Components/Icons/CheckCircleIcon.vue';
import XCircleIcon from '@/Components/Icons/XCircleIcon.vue';
import { trans } from '../mixins/i18n.js';

const props = defineProps({
    label: String,
    disabled: Boolean,
    result: {
        default: null,
        validator: (value) => value === null || typeof value === 'boolean',
    },
    selectedDeficiencies: { type: Array, default: () => [] },
    deficiencies: { type: Array, default: () => [] },
});

const emit = defineEmits(['change', 'update:deficiencies']);

const isExpanded = ref(props.result === false);

const stateClasses = computed(() => {
    if (props.result === true) {
        return {
            header: 'border-green/30 bg-green/10 text-green',
            sideBar: 'border-green/30',
            hint: 'text-green',
        };
    }
    if (props.result === false) {
        return {
            header: 'border-red/30 bg-red/10 text-red',
            sideBar: 'border-red/30',
            hint: 'text-red',
        };
    }
    return {
        header: 'border-accent/30 bg-button-primary text-primary',
        sideBar: 'border-accent/30',
        hint: 'text-gray-600',
    };
});

// Numbered translation keys (.actions.0, .actions.1, …) collected into an ordered list.
const warningActions = computed(() => {
    const prefix = 'tasks.safety_inspection.deficiencies_warning.actions.';
    const language = usePage().props.language ?? {};
    return Object.keys(language)
        .filter((key) => key.startsWith(prefix))
        .sort((a, b) => Number(a.slice(prefix.length)) - Number(b.slice(prefix.length)))
        .map((key) => language[key]);
});

const isSelected = (id) => props.selectedDeficiencies.includes(id);

const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
};

const confirmStep = () => {
    if (props.disabled) return;
    emit('change', true);
    emit('update:deficiencies', []);
    isExpanded.value = false;
};

const rejectStep = () => {
    if (props.disabled) return;
    emit('change', false);
    if (!isExpanded.value) {
        isExpanded.value = true;
    }
};

const toggleDeficiency = (id) => {
    if (props.disabled) return;
    const next = isSelected(id) ? props.selectedDeficiencies.filter((item) => item !== id) : [...props.selectedDeficiencies, id];
    emit('update:deficiencies', next);
};
</script>
